import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

export const DEFAULT_BRAND_COLORS = {
  primary: '#2781F6',
  secondary: '#6E56CF',
};

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

const normalizeHexColor = color => {
  if (!HEX_COLOR_PATTERN.test(color || '')) return null;

  const hex = color.slice(1);
  if (hex.length === 3) {
    return `#${hex
      .split('')
      .map(channel => channel + channel)
      .join('')}`.toUpperCase();
  }

  return color.toUpperCase();
};

const hexToRgbChannels = color => {
  const normalizedColor = normalizeHexColor(color);
  if (!normalizedColor) return null;

  const hex = normalizedColor.slice(1);
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ].join(' ');
};

// Converts a normalized 6-char hex to HSL (h in degrees, s and l in [0,1])
const hexToHsl = color => {
  const hex = normalizeHexColor(color).slice(1);
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) return [0, 0, l];

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === r) h = ((g - b) / delta + 6) % 6;
  else if (max === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;

  return [h * 60, s, l];
};

// Converts HSL (h degrees, s and l in [0,1]) to a "R G B" space-separated string
const hslToRgbChannels = (h, s, l) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ].join(' ');
};

// Returns shade variants as CSS variable value strings ("R G B")
// Shades 100-300 are lighter; 600-800 are darker
const generateColorShades = hex => {
  const [h, s, l] = hexToHsl(hex);
  return {
    100: hslToRgbChannels(h, s, Math.min(l + 0.35, 0.95)),
    200: hslToRgbChannels(h, s, Math.min(l + 0.2, 0.95)),
    300: hslToRgbChannels(h, s, Math.min(l + 0.1, 0.95)),
    600: hslToRgbChannels(h, s, Math.max(l - 0.1, 0.05)),
    700: hslToRgbChannels(h, s, Math.max(l - 0.2, 0.05)),
    800: hslToRgbChannels(h, s, Math.max(l - 0.3, 0.05)),
  };
};

export const getBrandColors = () => {
  const savedColors = LocalStorage.get(LOCAL_STORAGE_KEYS.BRAND_COLORS) || {};

  return {
    primary:
      normalizeHexColor(savedColors.primary) || DEFAULT_BRAND_COLORS.primary,
    secondary:
      normalizeHexColor(savedColors.secondary) ||
      DEFAULT_BRAND_COLORS.secondary,
  };
};

export const applyBrandColors = colors => {
  const brandColors = colors || getBrandColors();
  const { primary, secondary } = brandColors;

  document.documentElement.style.setProperty(
    '--brand-primary',
    hexToRgbChannels(primary)
  );
  document.documentElement.style.setProperty(
    '--brand-secondary',
    hexToRgbChannels(secondary)
  );

  // Apply dynamically generated shade variants for hover and shadow states
  const primaryShades = generateColorShades(primary);
  const secondaryShades = generateColorShades(secondary);

  Object.entries(primaryShades).forEach(([shade, value]) => {
    document.documentElement.style.setProperty(
      `--brand-primary-${shade}`,
      value
    );
  });

  Object.entries(secondaryShades).forEach(([shade, value]) => {
    document.documentElement.style.setProperty(
      `--brand-secondary-${shade}`,
      value
    );
  });
};

export const setBrandColors = (colors = {}) => {
  const currentColors = getBrandColors();
  const nextColors = {
    primary: normalizeHexColor(colors.primary) || currentColors.primary,
    secondary: normalizeHexColor(colors.secondary) || currentColors.secondary,
  };

  LocalStorage.set(LOCAL_STORAGE_KEYS.BRAND_COLORS, nextColors);
  applyBrandColors(nextColors);

  return nextColors;
};